import StyledToggleButtonGroup from "@/components/PageEditor/components/Viewport/components/Toolbar/components/StyledToggleButtonGroup/StyledToggleButtonGroup";
import LinkPopper from "@/components/PageEditor/editor-components/shared/LinkPopper";
import { ElementDatabaseService, PageService } from "@/services/admin-services";
import { DatabaseElement, Page } from "@/services/admin-services/models";
import { SelectionUtils } from "@/utils/SelectionUtils";
import { ROOT_NODE, useEditor } from "@craftjs/core";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ToggleButton from "@mui/material/ToggleButton";
import { useEffect, useMemo, useRef, useState } from "react";

const Toolbar = () => {
    const [enabled, setEnabled] = useState<boolean>(false);
    const [format, setFormat] = useState<string | null>(null);
    const selectionUtils = useRef<SelectionUtils>(new SelectionUtils());

    const [showAnchorPopper, setShowAnchorPopper] = useState<boolean>(false);
    const selectedAnchorRef = useRef<HTMLAnchorElement | null>(null);

    const pageService = useMemo(() => new PageService("int"), []);
    const elementService = useMemo(() => new ElementDatabaseService("int", "int-en"), []);

    const [pages, setPages] = useState<Array<Page>>();
    const [availablePages, setAvailablePages] = useState<Array<Page>>([]);
    const [elements, setElements] = useState<Array<DatabaseElement>>([]);

    const { language } = useEditor((state) => ({
        language: state.nodes[ROOT_NODE].data.props.language,
    }));

    useEffect(() => {
        document.addEventListener("selectionchange", onSelectionChange, true);

        pageService.getPages().then(setPages);
        elementService.get().then(setElements);

        return () => {
            const links = document.querySelectorAll(".craftjs-renderer a");
            links.forEach((link) => ((link as HTMLAnchorElement).style.backgroundColor = ""));

            document.removeEventListener("selectionchange", onSelectionChange, true);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!pages) return;

        setAvailablePages(pages.filter((p) => p.language === language));
    }, [language, pages]);

    const onSelectionChange = () => {
        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const baseNode = activeSelection.anchorNode as HTMLElement;
        if (!baseNode) return;

        const baseNodeParent = baseNode.parentElement as HTMLElement;
        if (
            (baseNode.closest && baseNode.closest(".craftjs-toolbar")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".craftjs-toolbar")) ||
            (baseNode.closest && baseNode.closest(".color-picker-overlay")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".color-picker-overlay")) ||
            (baseNode.closest && baseNode.closest(".link-popper")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".link-popper"))
        ) {
            setEnabled(false);
            return;
        }

        setEnabled(true);

        if ((activeSelection.type === "Range" || activeSelection.type === "Caret") && activeSelection.rangeCount > 0) {
            selectionUtils.current.save();
        } else if (activeSelection.type) {
            selectionUtils.current.clearSaved();
        }

        updateState();
    };

    const updateState = () => {
        updateFormatting();
        updateLink();
    };

    const updateFormatting = () => {
        setFormat(null);
    };

    const updateLink = () => {
        const links = document.querySelectorAll(".craftjs-renderer a");
        links.forEach((link) => ((link as HTMLAnchorElement).style.backgroundColor = ""));

        let anchorTag = selectionUtils.current.findParentTag("A") as HTMLAnchorElement | null;
        if (anchorTag) {
            if (anchorTag.closest(".craftjs-renderer")) {
                anchorTag.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
            } else {
                anchorTag = null;
            }
        }

        selectedAnchorRef.current = anchorTag;
        setShowAnchorPopper(Boolean(anchorTag));
    };

    const onToggleCommand = (cmd: string, showUi: boolean = false, value: string | undefined = undefined) => {
        selectionUtils.current.restore();
        document.execCommand(cmd, showUi, value);
        updateState();
    };

    const onHighlight = () => {
        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const node = document.createElement("span");
        node.appendChild(activeSelection.getRangeAt(0).cloneContents());

        if (node.innerHTML === "") return;
        const html = `<span class="text-primary-500">${node.innerHTML}</span>`;

        onToggleCommand("insertHTML", false, html);
    };

    const onRemoveHighlight = () => {
        let activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const node = document.createElement("span");
        node.appendChild(activeSelection.getRangeAt(0).cloneContents());

        document.execCommand("removeFormat");

        activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const parent = activeSelection.getRangeAt(0).commonAncestorContainer.parentElement as HTMLElement;
        setTimeout(() => {
            parent.classList.remove("text-primary-500");
        }, 100);
    };

    const onCreateLink = () => {
        setShowAnchorPopper(true);
    };

    const onEditLink = (href: string, value: string, openInNewTab: boolean, data: Record<string, string>) => {
        document.designMode = "on";

        setShowAnchorPopper(false);

        selectionUtils.current.restore();
        const anchorTag = selectionUtils.current.findParentTag("A") as HTMLAnchorElement;

        if (anchorTag) {
            selectionUtils.current.expandToTag(anchorTag);

            anchorTag.setAttribute("href", href);
            anchorTag.innerText = value || href;
            anchorTag.target = openInNewTab ? "_blank" : "_self";
        } else {
            document.execCommand("createLink", false, href);
        }

        const link = document.querySelector(`a[href="${href}"]`) as HTMLAnchorElement;
        if (link) {
            const selection = SelectionUtils.get();
            if (selection) {
                const selectedText = selection.getRangeAt(0).cloneContents().textContent;
                if (selectedText === href) {
                    link.innerHTML = value || href;
                }
            }

            link.target = openInNewTab ? "_blank" : "_self";

            if (data["internal-page-id"]) {
                const pageId = data["internal-page-id"];
                link.setAttribute("data-internal-page-id", pageId);
                link.removeAttribute("data-internal-element-slug");
            } else if (data["internal-element-slug"]) {
                const elementSlug = data["internal-element-slug"];
                link.setAttribute("data-internal-element-slug", elementSlug);
                link.removeAttribute("data-internal-page-id");
            } else {
                link.removeAttribute("data-internal-element-slug");
                link.removeAttribute("data-internal-page-id");
            }
        }

        document.designMode = "off";

        selectionUtils.current.collapseToEnd();
    };

    const onRemoveLink = () => {
        setShowAnchorPopper(false);

        const anchorTag = selectionUtils.current.findParentTag("A");
        if (!anchorTag) return;

        selectionUtils.current.expandToTag(anchorTag);
        anchorTag.style.backgroundColor = "";
        document.execCommand("unlink");

        selectionUtils.current.collapseToEnd();
    };

    return (
        <>
            <StyledToggleButtonGroup
                disabled={!enabled}
                size="small"
                value={format}
                exclusive
                aria-label="slogan formatting"
            >
                <ToggleButton
                    value="highlight"
                    aria-label="highlight"
                    disabled={format !== null}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onHighlight();
                    }}
                >
                    <FormatColorTextIcon />
                </ToggleButton>
                <ToggleButton
                    value="clear"
                    aria-label="clear"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onRemoveHighlight();
                    }}
                >
                    <FormatColorResetIcon />
                </ToggleButton>
            </StyledToggleButtonGroup>

            <StyledToggleButtonGroup disabled={!enabled} size="small" aria-label="links">
                {!selectedAnchorRef.current ? (
                    <ToggleButton
                        value="insert link"
                        aria-label="insert link"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onCreateLink();
                        }}
                    >
                        <LinkIcon />
                    </ToggleButton>
                ) : (
                    <ToggleButton
                        value="remove link"
                        aria-label="remove link"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onRemoveLink();
                        }}
                    >
                        <LinkOffIcon />
                    </ToggleButton>
                )}
            </StyledToggleButtonGroup>
            {showAnchorPopper && (
                <LinkPopper
                    active={showAnchorPopper}
                    anchorEl={selectedAnchorRef.current}
                    onEdit={onEditLink}
                    onRemove={onRemoveLink}
                    availablePages={availablePages}
                    availableElements={elements}
                />
            )}
        </>
    );
};

export default Toolbar;
